import { lightFormat, parseISO } from 'date-fns';
import React, { useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { usePrevious } from '_hooks';
import { selectError, selectLoading } from '_store/currentTransaction/selector';
import { updateBankTransaction } from '_store/currentTransaction/slice';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectActiveMainProductSource } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';
import EditTransactionForm from 'components/composed/forms/EditTransactionForm';

function parseTotalValue(value) {
  return value?.replace(/[$,]/g, '');
}

function parseDateValue(value = '') {
  return parseISO(value?.split('T')[0]);
}

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const UpdateTransactionModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.transactionUpdate));
  const data = useSelector(selectModalDataByKey(MODAL_KEYS.transactionUpdate));
  const loading = useSelector(selectLoading);
  const prevLoading = usePrevious(loading);
  const error = useSelector(selectError);
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const source = useSelector(selectActiveMainProductSource);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.transactionUpdate }));
  };

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        const { transactionType, date, accountName, contactName, reference, reconcile, total } = values || {};
        dispatch(
          updateBankTransaction({
            customerId,
            productId,
            transactionId: data?.id,
            body: {
              source,
              type: transactionType,
              date: lightFormat(date, "yyyy-MM-dd'T'HH:mm:ss"),
              accountName,
              contactName,
              reference,
              reconcile,
              currencyCode: 'USD',
              total: parseTotalValue(total),
            },
          })
        );
      }
    },
    [dispatch, customerId, productId, source, data?.id]
  );

  useEffect(() => {
    if (prevLoading && !loading && !error) {
      dispatch(hideModal({ key: MODAL_KEYS.transactionUpdate }));
    }
  }, [dispatch, prevLoading, loading, error]);

  return (
    <Modal
      dialogClassName="update-transaction-modal"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Update Transaction
        </Text>
        <EditTransactionForm
          error={error}
          loading={loading}
          submitDisabled={loading}
          defaultValues={{
            [FIELD_KEYS.source]: data[FIELD_KEYS.source],
            [FIELD_KEYS.transactionType]: data[FIELD_KEYS.type],
            [FIELD_KEYS.date]: parseDateValue(data[FIELD_KEYS.date]),
            [FIELD_KEYS.accountName]: data[FIELD_KEYS.accountName],
            [FIELD_KEYS.contactName]: data[FIELD_KEYS.contactName],
            [FIELD_KEYS.reference]: data[FIELD_KEYS.reference],
            [FIELD_KEYS.reconcile]: data[FIELD_KEYS.reconcile],
            [FIELD_KEYS.total]: data[FIELD_KEYS.total],
          }}
          onCancel={handleClose}
          onSubmit={handleSubmit}
        />
      </StyledModalBody>
    </Modal>
  );
};

export default UpdateTransactionModal;
